<!-- src/views/admin/Dashboard.vue -->
<template>
  <admin-layout title="Tableau de bord">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>
    
    <div v-else>
      <!-- Statistiques -->
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-primary-100 rounded-md p-3">
                <svg class="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">
                    Clients
                  </dt>
                  <dd class="flex items-baseline">
                    <div class="text-2xl font-semibold text-gray-900">
                      {{ dashboard.stats.clientCount }}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-4 sm:px-6">
            <div class="text-sm">
              <router-link to="/admin/clients" class="font-medium text-primary-600 hover:text-primary-500">
                Voir tous les clients
              </router-link>
            </div>
          </div>
        </div>
        
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-green-100 rounded-md p-3">
                <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">
                    Dispositifs
                  </dt>
                  <dd class="flex items-baseline">
                    <div class="text-2xl font-semibold text-gray-900">
                      {{ dashboard.stats.deviceCount }}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-4 sm:px-6">
            <div class="text-sm">
              <router-link to="/admin/devices" class="font-medium text-primary-600 hover:text-primary-500">
                Gérer les dispositifs
              </router-link>
            </div>
          </div>
        </div>
        
        <div class="bg-white overflow-hidden shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-red-100 rounded-md p-3">
                <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-gray-500 truncate">
                    Dispositifs hors ligne
                  </dt>
                  <dd class="flex items-baseline">
                    <div class="text-2xl font-semibold text-gray-900">
                      {{ dashboard.stats.offlineCount }}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-4 sm:px-6">
            <div class="text-sm">
              <router-link to="/admin/devices?filter=offline" class="font-medium text-red-600 hover:text-red-500">
                Voir les dispositifs hors ligne
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Comptes dont le titulaire a demandé la suppression (B9), échéance la plus proche d'abord -->
      <div v-if="dashboard.suppressions && dashboard.suppressions.length" class="mb-8 rounded-lg border border-red-300 bg-red-50 p-4">
        <h3 class="text-base font-semibold text-red-800">Suppressions de compte en cours ({{ dashboard.suppressions.length }})</h3>
        <p class="mt-1 text-xs text-red-700">Comptes suspendus à la demande de leur titulaire : aucune alerte ne part, effacement à l'échéance.</p>
        <ul class="mt-2 divide-y divide-red-200 text-sm">
          <li v-for="s in dashboard.suppressions" :key="s.id" class="flex items-center justify-between py-2">
            <router-link :to="`/admin/clients/${s.id}`" class="font-medium text-red-800 hover:underline">{{ s.name }}</router-link>
            <span class="text-red-700">effacé le {{ new Date(s.effacement_le).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'long', timeStyle: 'short' }) }}</span>
          </li>
        </ul>
      </div>

      <!-- Cartes provisionnées en atelier, en attente d'un client -->
      <unassigned-devices />

      <!-- Derniers événements -->
      <event-list 
        :events="dashboard.latestEvents" 
        title="Derniers événements" 
        :show-device-info="true"
        :show-ident="true"
        more-link="/admin/events"
      />
    </div>
  </admin-layout>
</template>

<script>
import { defineComponent, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import AdminLayout from '@/components/layout/AdminLayout.vue';
import EventList from '@/components/common/EventList.vue';
import UnassignedDevices from '@/components/admin/UnassignedDevices.vue';

export default defineComponent({
  name: 'AdminDashboard',
  
  components: {
    AdminLayout,
    EventList,
    UnassignedDevices
  },
  
  setup() {
    const store = useStore();
    
    const loading = ref(true);
    const dashboard = ref({
      stats: {
        clientCount: 0,
        deviceCount: 0,
        offlineCount: 0
      },
      suppressions: [],
      latestEvents: []
    });
    
    onMounted(async () => {
      try {
        const response = await store.dispatch('admin/fetchDashboard');
        dashboard.value = response;
      } catch (error) {
        console.error('Erreur lors du chargement du tableau de bord:', error);
      } finally {
        loading.value = false;
      }
    });
    
    return {
      loading,
      dashboard
    };
  }
});
</script>